/**
 * Amounts are stored as integer cents, exactly like loot-core's `amount`
 * column, so every display path funnels through here.
 */
export type CurrencyStyle = {
  symbol: string;
  code: string;
  separator: string;
  decimal: string;
  currencyInFront: boolean;
  showSymbol: boolean;
};

export const DEFAULT_CURRENCY: CurrencyStyle = {
  symbol: '¥',
  code: 'CNY',
  separator: ',',
  decimal: '.',
  currencyInFront: true,
  showSymbol: false,
};

const USD_CURRENCY: CurrencyStyle = {
  symbol: '$',
  code: 'USD',
  separator: ',',
  decimal: '.',
  currencyInFront: true,
  showSymbol: false,
};

export const EUR_CURRENCY: CurrencyStyle = {
  ...DEFAULT_CURRENCY,
  symbol: '€',
  code: 'EUR',
  separator: '.',
  decimal: ',',
  currencyInFront: false,
};

export const YEN_CURRENCY: CurrencyStyle = {
  ...DEFAULT_CURRENCY,
  symbol: '¥',
  code: 'JPY',
};

const CURRENCIES: Record<string, CurrencyStyle> = {
  CNY: DEFAULT_CURRENCY,
  USD: USD_CURRENCY,
  EUR: EUR_CURRENCY,
  GBP: { ...DEFAULT_CURRENCY, symbol: '£', code: 'GBP' },
  JPY: YEN_CURRENCY,
};

export function getCurrency(code: string | undefined): CurrencyStyle {
  return (code && CURRENCIES[code]) || DEFAULT_CURRENCY;
}

function groupDigits(
  digits: string,
  separator: string,
  showSeparator: boolean,
): string {
  if (!showSeparator || separator === '') return digits;
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/** 123456 -> "1,234.56" (cents in, display string out). */
export function amountToCurrency(
  amount: number,
  currency: CurrencyStyle = DEFAULT_CURRENCY,
  options: { showSymbol?: boolean; signed?: boolean } = {},
): string {
  const negative = amount < 0;
  const absolute = Math.abs(Math.round(amount));
  const whole = Math.floor(absolute / 100);
  const fraction = String(absolute % 100).padStart(2, '0');

  const number = `${groupDigits(String(whole), currency.separator, true)}${
    currency.decimal
  }${fraction}`;

  const sign = negative ? '-' : options.signed && amount > 0 ? '+' : '';
  const symbol =
    (options.showSymbol ?? currency.showSymbol) && currency.symbol
      ? currency.currencyInFront
        ? currency.symbol
        : ''
      : '';
  const trailingSymbol =
    (options.showSymbol ?? currency.showSymbol) &&
    currency.symbol &&
    !currency.currencyInFront
      ? currency.symbol
      : '';

  return `${sign}${symbol}${number}${trailingSymbol}`;
}

/** "1,234.56" -> 123456. Returns null when the text is not a number. */
export function currencyToAmount(
  value: string,
  currency: CurrencyStyle = DEFAULT_CURRENCY,
): number | null {
  const cleaned = value
    .replace(/[^0-9.,\-]/g, '')
    .replace(new RegExp(`\\${currency.separator}`, 'g'), '')
    .replace(new RegExp(`\\${currency.decimal}`, 'g'), '.');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const parsed = Number.parseFloat(cleaned);
  if (Number.isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

export type FormatType =
  | 'financial'
  | 'financial-with-sign'
  | 'financial-hidden'
  | 'percentage'
  | 'number';

export function format(
  amount: number | null | undefined,
  type: FormatType = 'financial',
  currency: CurrencyStyle = DEFAULT_CURRENCY,
  hideAmounts = false,
): string {
  if (amount === null || amount === undefined) return '';
  switch (type) {
    case 'percentage':
      return `${Math.round(amount * 10000) / 100}%`;
    case 'number':
      return String(amount);
    case 'financial-hidden':
      return hideAmounts ? '••••' : amountToCurrency(amount, currency);
    case 'financial-with-sign':
      return hideAmounts
        ? '••••'
        : amountToCurrency(amount, currency, { signed: true });
    case 'financial':
    default:
      return hideAmounts ? '••••' : amountToCurrency(amount, currency);
  }
}
